import React from 'react';
import { cn } from '@/lib/utils';
import { TestItemComment } from 'store/src/lib/services/types';
import AuthorBadge from '@/components/raytd/user-avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2 } from 'lucide-react';
import formatDateAndTime from '@/utils/formatDateAndTime';
import { useSetTestItemCommentResolvedMutation, useDeleteTestItemCommentMutation } from '@app.raytd.com/store';
import { toast } from 'sonner';

interface CommentItemProps {
  comment: TestItemComment;
  testItemId: number;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, testItemId }) => {
  const [setResolved, { isLoading: isResolving }] = useSetTestItemCommentResolvedMutation();
  const [deleteComment, { isLoading: isDeleting }] = useDeleteTestItemCommentMutation();

  const isResolved = comment.resolved_at !== null;
  const canDelete = comment.can_delete === true;

  const handleToggleResolved = async () => {
    try {
      await setResolved({
        testItemId,
        commentId: comment.id,
        resolved: !isResolved,
      }).unwrap();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update comment status');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await deleteComment({
        testItemId,
        commentId: comment.id,
      }).unwrap();
      toast.success('Comment deleted');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to delete comment');
    }
  };

  const resolvedDate = comment.resolved_at ? formatDateAndTime(comment.resolved_at) : null;
  const createdDate = formatDateAndTime(comment.created_at);

  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-3 rounded-md border',
        isResolved
          ? 'bg-zinc-50 border-zinc-200 opacity-75'
          : 'bg-white border-zinc-300'
      )}
    >
      <div className="flex flex-row items-start justify-between gap-2">
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex flex-row items-center gap-2">
            {comment.creator && (
              <AuthorBadge item={comment.creator} size="small" />
            )}
            <div className="flex flex-col text-xs text-zinc-500">
              {comment.creator && (
                <span className="font-medium">{comment.creator.name}</span>
              )}
              <span>{createdDate}</span>
            </div>
          </div>

          <div className={cn('text-sm text-zinc-950', isResolved && 'line-through')}>
            {comment.content}
          </div>

          {isResolved && resolvedDate && (
            <div className="text-xs text-zinc-500 italic">
              {comment.resolver
                ? `Resolved by ${comment.resolver.name} ${resolvedDate}`
                : `Resolved ${resolvedDate}`}
            </div>
          )}
        </div>

        <div className="flex flex-row items-start gap-2">
          <Checkbox
            checked={isResolved}
            onCheckedChange={handleToggleResolved}
            disabled={isResolving}
            aria-label={isResolved ? 'Mark as unresolved' : 'Mark as resolved'}
          />
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-zinc-500 hover:text-red-600"
              onClick={handleDelete}
              disabled={isDeleting}
              aria-label="Delete comment"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(CommentItem);
